interface AnchorFigureProps {
  anchorId: string;
  size?: number;
}

/// Anchor figure. For seven of the nine anchors we serve a real
/// Wikimedia Commons photo (cleaner specimen / studio shots,
/// licensed CC-BY-SA / CC-BY / PD — see ANCHOR_PHOTO_CREDITS for
/// per-image attribution). For Delta and Manson Supreme there are
/// no usable Commons photos so we fall back to the stylised SVG
/// silhouette drawn in `renderAnchor`. Custom-shot replacements
/// for all nine are on the roadmap.
const VB_W = 160;
const VB_H = 130;

/// Photo filenames in `apps/web/public/anchors/`. Keys without a
/// matching entry fall through to the SVG silhouette. When custom
/// photos become available, drop them into the same folder and add
/// the entry here.
const ANCHOR_PHOTOS: Record<string, string> = {
  cqr: "/anchors/cqr.jpg",
  rocna: "/anchors/rocna.jpg",
  bruce: "/anchors/bruce.jpg",
  danforth: "/anchors/danforth.jpg",
  fisherman: "/anchors/fisherman.jpg",
  mushroom: "/anchors/mushroom.jpg",
  grapnel: "/anchors/grapnel.jpg",
};

/// Attribution lines — per Commons licence requirements. Surfaced
/// in a credits panel on the Reference / Anchoring pages.
export const ANCHOR_PHOTO_CREDITS: ReadonlyArray<{
  anchorId: string;
  credit: string;
  licence: string;
  source: string;
}> = [
  {
    anchorId: "cqr",
    credit: "C Smith",
    licence: "Public Domain",
    source: "https://commons.wikimedia.org/wiki/File:Genuine_CQR.jpg",
  },
  {
    anchorId: "rocna",
    credit: "Rocna Anchors",
    licence: "Attribution",
    source: "https://commons.wikimedia.org/wiki/File:Genuine_Rocna.jpg",
  },
  {
    anchorId: "bruce",
    credit: "Bill Lionheart (Billlion)",
    licence: "CC BY-SA 3.0",
    source:
      "https://commons.wikimedia.org/wiki/File:Genuine_Bruce_anchor.jpg",
  },
  {
    anchorId: "danforth",
    credit: "XoRys",
    licence: "Public Domain",
    source: "https://commons.wikimedia.org/wiki/File:Danforth_anchor.jpg",
  },
  {
    anchorId: "fisherman",
    credit: "Mark Murphy",
    licence: "Public Domain",
    source: "https://commons.wikimedia.org/wiki/File:Admiralty_anchor.JPG",
  },
  {
    anchorId: "mushroom",
    credit: "MaciejKa",
    licence: "CC BY-SA 3.0",
    source: "https://commons.wikimedia.org/wiki/File:Kotwica_grzybkowa.jpg",
  },
  {
    anchorId: "grapnel",
    credit: "Ulfbastel",
    licence: "Public Domain",
    source: "https://commons.wikimedia.org/wiki/File:Klappdraggen.jpg",
  },
];

export function AnchorFigure({ anchorId, size = 160 }: AnchorFigureProps) {
  const photo = ANCHOR_PHOTOS[anchorId];
  if (photo) {
    return (
      <img
        src={photo}
        alt={`${anchorId} anchor (Wikimedia Commons)`}
        width={size}
        height={(size * VB_H) / VB_W}
        style={{
          display: "block",
          width: size,
          // Photos have varying aspect ratios; let them fit into a
          // box of the same size as the SVG fallback so the gallery
          // grid stays even.
          height: (size * VB_H) / VB_W,
          objectFit: "contain",
          borderRadius: 4,
          background: "#f8fafc",
        }}
        loading="lazy"
      />
    );
  }
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={size}
      height={(size * VB_H) / VB_W}
      style={{ display: "block" }}
      role="img"
      aria-label={`${anchorId} anchor diagram`}
    >
      <defs>
        <linearGradient id={`steel-${anchorId}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6b7d92" />
          <stop offset="55%" stopColor="#41526a" />
          <stop offset="100%" stopColor="#222a37" />
        </linearGradient>
        <linearGradient id={`steel-edge-${anchorId}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#222a37" />
          <stop offset="50%" stopColor="#41526a" />
          <stop offset="100%" stopColor="#222a37" />
        </linearGradient>
      </defs>
      <g
        stroke="#0b121b"
        strokeWidth={0.7}
        strokeLinejoin="round"
        strokeLinecap="round"
      >
        {renderAnchor(anchorId)}
      </g>
    </svg>
  );
}

function fill(id: string) {
  return `url(#steel-${id})`;
}
function edgeFill(id: string) {
  return `url(#steel-edge-${id})`;
}

function renderAnchor(id: string) {
  switch (id) {
    case "cqr":
      return <Cqr id={id} />;
    case "delta":
      return <Delta id={id} />;
    case "rocna":
      return <Rocna id={id} />;
    case "manson_supreme":
      return <MansonSupreme id={id} />;
    case "bruce":
      return <Bruce id={id} />;
    case "danforth":
      return <Danforth id={id} />;
    case "fisherman":
      return <Fisherman id={id} />;
    case "mushroom":
      return <Mushroom id={id} />;
    case "grapnel":
      return <Grapnel id={id} />;
    default:
      return <Cqr id={id} />;
  }
}

/// Generic eye + shackle at the chain-attachment end of the shank,
/// drawn at an arbitrary point along the shank. `angle` rotates
/// the eye so it sits perpendicular to the shank line.
function Eye({
  cx,
  cy,
  id,
  angle = 0,
}: {
  cx: number;
  cy: number;
  id: string;
  angle?: number;
}) {
  return (
    <g transform={`rotate(${angle} ${cx} ${cy})`}>
      <ellipse cx={cx} cy={cy} rx={7} ry={5} fill={fill(id)} />
      <ellipse cx={cx} cy={cy} rx={3} ry={2} fill="#0b121b" />
    </g>
  );
}

// ─── Modern scoop family ──────────────────────────────────────

function RocnaShank({ id, withSlot }: { id: string; withSlot: boolean }) {
  // Long shank with a gentle elbow bend: from eye at top-left,
  // down to the fluke attachment near the middle-bottom. Drawn as
  // a tapered flat strap with darker side edges to suggest
  // section thickness.
  const shank = (
    <>
      <path
        d="M 18 14
           L 30 10
           L 90 60
           L 96 70
           L 90 76
           L 82 70
           L 28 24
           Z"
        fill={fill(id)}
      />
      {/* Inner highlight stripe down the centre of the shank */}
      <path
        d="M 24 14 L 86 68"
        stroke="#9aabbf"
        strokeWidth={0.9}
        opacity={0.4}
        fill="none"
      />
    </>
  );
  if (!withSlot) return shank;
  return (
    <>
      {shank}
      {/* Trademark Manson slot — long oblong cut down the centre */}
      <rect
        x={36}
        y={20}
        width={42}
        height={3.5}
        rx={1.5}
        fill="#0b121b"
        transform="rotate(40 57 22)"
      />
    </>
  );
}

function RocnaFluke({ id }: { id: string }) {
  return (
    <>
      {/* Triangular flat plate fluke — toe pointed forward-down
          (lower-left), back edge curving up-right where the
          roll-bar foot attaches. Drawn as two stacked layers so
          the bottom surface reads as darker (in shadow). */}
      <path
        d="M 88 72
           L 152 72
           L 150 86
           L 96 102
           L 50 110
           L 28 102
           L 38 90
           L 64 80 Z"
        fill={fill(id)}
      />
      {/* Lower shadow line under the fluke */}
      <path
        d="M 28 102 L 50 110 L 96 102 L 150 86"
        fill="none"
        stroke="#0b121b"
        strokeWidth={1.2}
        opacity={0.55}
      />
      {/* Skids — three small fins protruding below the bottom
          edge of the fluke. The skids are what stops the anchor
          rolling on its side and is a Rocna trademark. */}
      <path d="M 48 108 L 52 116 L 56 108 Z" fill={edgeFill(id)} />
      <path d="M 76 110 L 80 118 L 84 110 Z" fill={edgeFill(id)} />
      <path d="M 104 106 L 108 114 L 112 106 Z" fill={edgeFill(id)} />
      {/* Pointed toe lip on the front-bottom edge */}
      <path d="M 28 102 L 22 106 L 32 108 Z" fill={edgeFill(id)} />
      {/* Concave-fluke highlight running across the middle */}
      <path
        d="M 50 88 C 80 78 120 80 142 82"
        fill="none"
        stroke="#9aabbf"
        strokeWidth={0.8}
        opacity={0.45}
      />
    </>
  );
}

function RollBar({ id }: { id: string }) {
  // Signature Rocna/Manson roll-bar: a thick arched steel bar
  // running from the back of the shank (top of the fluke) over
  // to the rear lip of the fluke. Drawn as a thick stroke with a
  // lighter inner stroke to suggest a polished steel finish.
  return (
    <>
      <path
        d="M 92 70
           Q 138 32 152 78"
        fill="none"
        stroke="#41526a"
        strokeWidth={6}
        strokeLinecap="round"
      />
      <path
        d="M 92 70
           Q 138 32 152 78"
        fill="none"
        stroke="#9aabbf"
        strokeWidth={1.8}
        strokeLinecap="round"
        opacity={0.55}
      />
    </>
  );
}

function Rocna({ id }: { id: string }) {
  return (
    <>
      <Eye cx={20} cy={14} id={id} angle={45} />
      <RollBar id={id} />
      <RocnaFluke id={id} />
      <RocnaShank id={id} withSlot={false} />
    </>
  );
}

function MansonSupreme({ id }: { id: string }) {
  return (
    <>
      <Eye cx={20} cy={14} id={id} angle={45} />
      <RollBar id={id} />
      <RocnaFluke id={id} />
      <RocnaShank id={id} withSlot={true} />
    </>
  );
}

// ─── CQR / Delta plough family ─────────────────────────────────

function Cqr({ id }: { id: string }) {
  return (
    <>
      {/* Eye at upper-right — CQR shank usually drawn horizontal
          with the plough hanging at the lower-left. */}
      <Eye cx={148} cy={24} id={id} angle={75} />
      {/* Shank — curving from eye down-left to the hinge */}
      <path
        d="M 138 18
           L 148 24
           L 152 32
           L 144 32
           Q 100 36 70 48
           L 64 56
           L 74 60
           Q 110 44 138 32
           Z"
        fill={fill(id)}
      />
      {/* Hinge knuckle — the iconic CQR pivot between shank and
          plough share. Drawn as a chunky pin so it pops. */}
      <circle cx={62} cy={60} r={6} fill={edgeFill(id)} />
      <circle cx={62} cy={60} r={2.2} fill="#0b121b" />
      {/* Plough share — a curved single blade like a moldboard,
          coming to a pointed toe at the lower-left. The "heart"
          shape characteristic of a CQR plough. */}
      <path
        d="M 62 64
           C 48 64 32 70 22 84
           C 14 96 18 110 32 116
           C 50 120 80 116 96 104
           C 102 92 92 76 78 68
           C 72 66 66 64 62 64 Z"
        fill={fill(id)}
      />
      {/* Pointed toe at the leading edge */}
      <path
        d="M 22 84 L 12 92 L 24 92 Z"
        fill={edgeFill(id)}
      />
      {/* Centre keel ridge running up the share */}
      <path
        d="M 62 64
           Q 56 90 36 112"
        fill="none"
        stroke="#0b121b"
        strokeWidth={1}
        opacity={0.55}
      />
      {/* Highlight along the wing */}
      <path
        d="M 56 70 Q 70 80 88 86"
        fill="none"
        stroke="#9aabbf"
        strokeWidth={0.7}
        opacity={0.45}
      />
    </>
  );
}

function Delta({ id }: { id: string }) {
  return (
    <>
      <Eye cx={148} cy={24} id={id} angle={75} />
      {/* Rigid one-piece shank — no hinge, merges smoothly into
          the plough share */}
      <path
        d="M 138 18
           L 148 24
           L 152 32
           L 144 32
           Q 96 42 62 64
           L 56 76
           L 70 78
           Q 110 50 138 32 Z"
        fill={fill(id)}
      />
      {/* Triangular plough fluke — sharper / more angular than
          CQR, no hinge. */}
      <path
        d="M 64 70
           L 18 110
           L 34 120
           L 90 110
           L 78 80 Z"
        fill={fill(id)}
      />
      <path
        d="M 18 110 L 10 118 L 22 116 Z"
        fill={edgeFill(id)}
      />
      {/* Centre ridge */}
      <path
        d="M 64 72 Q 50 96 32 118"
        fill="none"
        stroke="#0b121b"
        strokeWidth={1}
        opacity={0.55}
      />
    </>
  );
}

// ─── Bruce ─────────────────────────────────────────────────────

function Bruce({ id }: { id: string }) {
  return (
    <>
      <Eye cx={14} cy={20} id={id} angle={45} />
      {/* Slim horizontal shank with a gentle bend near the claws —
          attaches to the back of the three-claw casting. */}
      <path
        d="M 18 16
           L 26 12
           L 102 64
           L 96 76
           L 88 70
           L 20 22 Z"
        fill={fill(id)}
      />
      {/* Three-claw cast piece — large central claw projecting
          down-right + two outer claws splaying out left and
          right. Drawn as a single path so the cast feel is
          preserved. */}
      <path
        d="M 96 70
           L 110 70
           L 124 86
           L 138 78
           L 152 92
           L 138 106
           L 122 100
           L 108 116
           L 96 124
           L 84 116
           L 70 104
           L 58 110
           L 46 104
           L 60 88
           L 80 78 Z"
        fill={fill(id)}
      />
      {/* Three-claw silhouette — overlay strokes to emphasise the
          three distinct lobes (centre + outer-left + outer-right). */}
      <path
        d="M 96 124 L 96 100 M 64 102 L 76 92 M 134 96 L 122 86"
        stroke="#0b121b"
        strokeWidth={0.9}
        opacity={0.5}
        fill="none"
      />
      {/* Highlight on the upper face of the central claw */}
      <path
        d="M 92 76 L 98 108"
        stroke="#9aabbf"
        strokeWidth={0.8}
        opacity={0.45}
      />
    </>
  );
}

// ─── Danforth (fluke / Fortress family) ────────────────────────

function Danforth({ id }: { id: string }) {
  return (
    <>
      {/* Long slim shank with the eye at the top — for the fluke
          family the iconic angle is "anchor stood on its crown",
          showing the twin flukes splayed out at the bottom. */}
      <Eye cx={80} cy={10} id={id} />
      <rect x={78} y={14} width={4} height={68} fill={fill(id)} />
      {/* Crown stock — wide horizontal bar with knob ends. */}
      <rect x={18} y={80} width={124} height={6} rx={2} fill={fill(id)} />
      <circle cx={18} cy={83} r={4} fill={edgeFill(id)} />
      <circle cx={142} cy={83} r={4} fill={edgeFill(id)} />
      {/* Two broad flat triangular flukes splaying out from the
          crown. Drawn with a clear pointed tip and straight
          inboard edges so they read as plate flukes. */}
      <path d="M 74 84 L 14 124 L 56 92 Z" fill={fill(id)} />
      <path d="M 86 84 L 146 124 L 104 92 Z" fill={fill(id)} />
      {/* Centre ridges on the flukes */}
      <path
        d="M 56 92 L 30 116 M 104 92 L 130 116"
        fill="none"
        stroke="#0b121b"
        strokeWidth={0.6}
        opacity={0.45}
      />
    </>
  );
}

// ─── Fisherman / Admiralty (classic) ───────────────────────────

function Fisherman({ id }: { id: string }) {
  return (
    <>
      <Eye cx={80} cy={8} id={id} />
      {/* Horizontal stock with Admiralty-pattern ball ends */}
      <rect x={36} y={16} width={88} height={5} rx={1.5} fill={fill(id)} />
      <circle cx={36} cy={18.5} r={6} fill={fill(id)} />
      <circle cx={124} cy={18.5} r={6} fill={fill(id)} />
      {/* Long straight shank passing through the stock down to
          the crown */}
      <rect x={77} y={12} width={6} height={70} fill={fill(id)} />
      <circle cx={80} cy={84} r={7.5} fill={edgeFill(id)} />
      {/* Two curving arms ending in pointed palm + bill flukes —
          the classic anchor-symbol silhouette */}
      <path
        d="M 80 84
           C 56 88 32 102 16 124
           L 36 116
           L 46 102
           C 56 96 66 92 76 90 Z"
        fill={fill(id)}
      />
      <path
        d="M 80 84
           C 104 88 128 102 144 124
           L 124 116
           L 114 102
           C 104 96 94 92 84 90 Z"
        fill={fill(id)}
      />
      {/* Pointed bill tip accents */}
      <path
        d="M 16 124 L 30 110 M 144 124 L 130 110"
        stroke="#0b121b"
        strokeWidth={0.9}
        opacity={0.55}
        fill="none"
      />
    </>
  );
}

// ─── Mushroom (mooring) ────────────────────────────────────────

function Mushroom({ id }: { id: string }) {
  return (
    <>
      <Eye cx={80} cy={10} id={id} />
      {/* Thick shank running down into the dome */}
      <rect x={75} y={14} width={10} height={68} fill={fill(id)} />
      {/* Mushroom dome — wide flared cap with a 3D feel via two
          arcs (top crown + base ellipse) */}
      <path
        d="M 14 96
           C 14 76 38 64 80 64
           C 122 64 146 76 146 96
           L 146 104
           C 146 116 122 122 80 122
           C 38 122 14 116 14 104 Z"
        fill={fill(id)}
      />
      {/* Top highlight to give the dome a polished feel */}
      <path
        d="M 30 84 C 50 72 110 72 130 84"
        fill="none"
        stroke="#9aabbf"
        strokeWidth={1.2}
        opacity={0.5}
      />
      {/* Underside shadow */}
      <ellipse cx={80} cy={114} rx={62} ry={6} fill="#0b121b" opacity={0.35} />
    </>
  );
}

// ─── Grapnel (folding multi-prong) ─────────────────────────────

function Grapnel({ id }: { id: string }) {
  return (
    <>
      <Eye cx={80} cy={8} id={id} />
      {/* Centre shank */}
      <rect x={76} y={12} width={8} height={60} fill={fill(id)} />
      {/* Hub where the prongs meet */}
      <circle cx={80} cy={74} r={7} fill={edgeFill(id)} />
      {/* Back-pair prongs (dimmer for depth) — drawn first so
          they sit behind the front prongs */}
      <path
        d="M 80 80
           C 74 96 74 110 78 124
           L 82 124
           C 86 110 86 96 80 80 Z"
        fill="#222a37"
        opacity={0.7}
      />
      {/* Front-left prong */}
      <path
        d="M 80 80
           C 68 92 48 106 22 124
           L 36 124
           C 56 110 70 96 80 86 Z"
        fill={fill(id)}
      />
      {/* Front-right prong */}
      <path
        d="M 80 80
           C 92 92 112 106 138 124
           L 124 124
           C 104 110 90 96 80 86 Z"
        fill={fill(id)}
      />
      {/* Front centre prong (straight down) */}
      <rect x={76} y={80} width={8} height={44} fill={fill(id)} />
      {/* Barb tips on each prong */}
      <path
        d="M 22 124 L 16 118 L 28 122 M 138 124 L 144 118 L 132 122 M 76 124 L 70 118 L 80 120 L 90 118 L 84 124"
        stroke="#0b121b"
        strokeWidth={0.7}
        opacity={0.55}
        fill="none"
      />
    </>
  );
}
